import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { fetchHistoricalEnrollmentsInfo } from '@actions/historicalMemberApplication/historicalApplicationsActionCreator';
import { fetchEnrollmentAndEnrolleeDetails } from '@actions/memberPortal/enrollmentsActionCreators';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { ALL } from '@constants/common/miscellaneous';
import useGetEnrollmentYears from '@hooks/useGetEnrollmentYears';
import { isHistoricalYear } from '@modules/historical-application/utils/ApplicationContainerUtils';
import { HistoricalEnrollmentsContent } from '@modules/members/historical-applications/components/Enrollments';
import { getCoverageYearsWithHistorical } from '@modules/members/historical-applications/utils';

import { environmentSelector, useAppDispatch, useAppSelector } from '../../utils';
import { IEnrollment } from '../common/types';
import { StyledMainSection } from '../styles/EnrollmentSharedStyled';
import AdditionalInformation from './AdditionalInformation';
import EnrollmentDetails from './EnrollmentDetails';
import EnrollmentHistoryActions from './EnrollmentActions';
import Header from './Header';
import TransactionActions from './TransactionActions';

const StyledCard = styled.div`
    border: ${({ theme }) => `1px solid ${theme.gray7}`};
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    margin-bottom: 20px;
    background-color: #fff;
`;

const StyledCardContent = styled.div`
    padding: 20px;
`;

const NoEnrollment = styled.div`
    text-align: center;
    margin-top: 20px;
`;

function EnrollmentHistory() {
    const dispatch: Dispatch<any> = useAppDispatch();
    const { id } = useParams();
    const [enrollments, setEnrollments] = useState<IEnrollment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
    const { enrollmentandenrolleedetails } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);
    const enrollmentYears = useGetEnrollmentYears(enrollmentandenrolleedetails);
    const { isState, exchangeStartYear, flags } = useAppSelector(environmentSelector);
    const showHistoricalData = flags?.showHistoricalData || false;
    const { historicalEnrollmentsInfo } = useAppSelector(state => state.historicalApplications);
    const historicalEnrollmentsData = historicalEnrollmentsInfo?.enrollments || [];
    const isSelectedYearHistorical = showHistoricalData && isHistoricalYear(Number(selectedYear), exchangeStartYear);
    const currentCoverageYear = enrollmentandenrolleedetails?.currentCoverageYear;
    const years = showHistoricalData ? getCoverageYearsWithHistorical(currentCoverageYear) : enrollmentYears;

    useEffect(() => {
        if (isSelectedYearHistorical) {
            setEnrollments([]);
            if (!id) {
                return;
            }
            dispatch(fetchHistoricalEnrollmentsInfo(selectedYear, '', id));
        } else {
            (async () => {
                setIsLoading(true);
                const formData = new FormData();
                formData.append('encHouseholdId', id || '');
                formData.append('csrftoken', window.csrf_token || '');
                await dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
                setIsLoading(false);
            })();
        }
    }, [id, selectedYear, exchangeStartYear, dispatch, isSelectedYearHistorical]);

    const getSortedEnrollments = useMemo(() => {
        let enrollments: IEnrollment[] = [];
        const enrollmentDataDtoListMap = enrollmentandenrolleedetails?.enrollmentDataDtoListMap;
        if (enrollmentDataDtoListMap) {
            for (const key in enrollmentDataDtoListMap) {
                const keyEnrollments = enrollmentDataDtoListMap[key];
                if (keyEnrollments && Array.isArray(keyEnrollments)) {
                    enrollments = enrollments.concat(keyEnrollments);
                }
            }
            enrollments.sort((a: IEnrollment, b: IEnrollment) => {
                return new Date(b.creationTimestamp).getTime() - new Date(a.creationTimestamp).getTime();
            });
        }

        return enrollments;
    }, [enrollmentandenrolleedetails]);

    useEffect(() => {
        const filteredEnrollments = selectedYear === ALL
            ? getSortedEnrollments
            : getSortedEnrollments?.filter(el => el.enrollmentBenefitEffectiveStartDate?.includes(selectedYear)) ?? [];
        setEnrollments(filteredEnrollments);
        setPermissions(enrollmentandenrolleedetails?.userPermissionList);
    }, [selectedYear, getSortedEnrollments, enrollmentandenrolleedetails]);

    return (
        <>
            <Header years={years} selectedYear={selectedYear} setSelectedYear={setSelectedYear} />

            <ConditionalRendering isTrue={isSelectedYearHistorical}>
                <div className="padding-3">
                    <HistoricalEnrollmentsContent enrollments={historicalEnrollmentsData} />
                </div>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!isSelectedYearHistorical}>
                <div className="padding-3">
                    <StyledMainSection hasNoPadding={isState.GA}>
                        <ConditionalRendering isTrue={enrollments.length === 0 && !isLoading}>
                            <NoEnrollment>
                                <p>
                                    <FormattedMessage id={'member.enrollment_list.noEnrollmentFound'} />
                                </p>
                            </NoEnrollment>
                        </ConditionalRendering>

                        <ConditionalRendering isTrue={enrollments.length > 0}>
                            {enrollments.map((enrollment, index) => (
                                <StyledCard key={enrollment.enrollmentId} data-testid={`enrollment-card-${index}`}>
                                    <StyledCardContent>
                                        <EnrollmentDetails enrollment={enrollment} dataTestId={`enrollment-details-${index}`} />
                                        <TransactionActions enrollment={enrollment} />
                                        <AdditionalInformation
                                            enrollment={enrollment}
                                            dataTestId={`additional-information-${index}`}
                                        />
                                    </StyledCardContent>
                                    <EnrollmentHistoryActions enrollment={enrollment} permissions={permissions} />
                                </StyledCard>
                            ))}
                        </ConditionalRendering>
                    </StyledMainSection>
                </div>
            </ConditionalRendering>
        </>
    );
}

export default EnrollmentHistory;
